import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useAdminTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeactivateTemplate,
} from '../../hooks/useAdminExtended';
import type { AdminTemplate } from '../../api/client';

interface TemplateFormData {
  name: string;
  category: string;
  htmlLayout: string;
  previewImageUrl: string;
}

const emptyForm: TemplateFormData = {
  name: '',
  category: '',
  htmlLayout: '',
  previewImageUrl: '',
};

export default function TemplateManagerPage() {
  const { data: templates, isLoading, error } = useAdminTemplates();
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deactivateMutation = useDeactivateTemplate();

  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AdminTemplate | null>(
    null,
  );
  const [form, setForm] = useState<TemplateFormData>(emptyForm);
  const [deactivateConfirmId, setDeactivateConfirmId] = useState<string | null>(
    null,
  );

  const filtered = templates?.filter((t) => {
    if (filter === 'active') return t.isActive;
    if (filter === 'inactive') return !t.isActive;
    return true;
  });

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (template: AdminTemplate) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      category: template.category,
      htmlLayout: template.htmlLayout,
      previewImageUrl: template.previewImageUrl,
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    if (editingTemplate) {
      await updateMutation.mutateAsync({
        id: editingTemplate.id,
        data: {
          name: form.name,
          category: form.category,
          htmlLayout: form.htmlLayout,
          previewImageUrl: form.previewImageUrl,
        },
      });
    } else {
      await createMutation.mutateAsync({
        name: form.name,
        category: form.category,
        htmlLayout: form.htmlLayout,
        previewImageUrl: form.previewImageUrl,
      });
    }
    handleClose();
  };

  const handleDeactivate = async (id: string) => {
    await deactivateMutation.mutateAsync(id);
    setDeactivateConfirmId(null);
  };

  const handleReactivate = async (template: AdminTemplate) => {
    await updateMutation.mutateAsync({
      id: template.id,
      data: { isActive: true },
    });
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load templates.
      </Alert>
    );
  }

  const formValid =
    form.name.trim() &&
    form.category.trim() &&
    form.htmlLayout.trim() &&
    form.previewImageUrl.trim();

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Template Manager
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ minHeight: 44 }}
        >
          Add Template
        </Button>
      </Box>

      <Tabs
        value={filter}
        onChange={(_e, v) => setFilter(v as typeof filter)}
        sx={{ mb: 3 }}
      >
        <Tab label="All" value="all" sx={{ minHeight: 44 }} />
        <Tab label="Active" value="active" sx={{ minHeight: 44 }} />
        <Tab label="Inactive" value="inactive" sx={{ minHeight: 44 }} />
      </Tabs>

      {isLoading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Skeleton variant="rounded" height={260} />
            </Grid>
          ))}
        </Grid>
      ) : filtered && filtered.length > 0 ? (
        <Grid container spacing={2}>
          {filtered.map((template) => (
            <Grid key={template.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: template.isActive ? 1 : 0.6,
                }}
              >
                <CardMedia
                  component="img"
                  height="140"
                  image={template.previewImageUrl}
                  alt={template.name}
                  sx={{ objectFit: 'cover' }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.src =
                      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE0MCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOTk5IiBmb250LXNpemU9IjE0Ij5ObyBQcmV2aWV3PC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
                <CardContent
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: { xs: 2, sm: 2.5 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        fontSize: { xs: 14, sm: 15 },
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {template.name}
                    </Typography>
                    <Chip
                      label={template.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={template.isActive ? 'success' : 'default'}
                      sx={{ fontSize: 11 }}
                    />
                  </Box>
                  <Chip
                    label={template.category}
                    size="small"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start', mb: 1.5, fontSize: 12 }}
                  />
                  <Box
                    sx={{
                      mt: 'auto',
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenEdit(template)}
                      sx={{ minHeight: 40 }}
                    >
                      Edit
                    </Button>
                    {template.isActive ? (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeactivateConfirmId(template.id)}
                        sx={{ minHeight: 40 }}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        color="success"
                        onClick={() => handleReactivate(template)}
                        sx={{ minHeight: 40 }}
                      >
                        Reactivate
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 6 }}
        >
          No templates found.
        </Typography>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Category"
              value={form.category}
              onChange={(e) =>
                setForm((p) => ({ ...p, category: e.target.value }))
              }
              fullWidth
              required
              size="small"
              placeholder="e.g. birthday, wedding, corporate"
            />
            <TextField
              label="Preview Image URL"
              value={form.previewImageUrl}
              onChange={(e) =>
                setForm((p) => ({ ...p, previewImageUrl: e.target.value }))
              }
              fullWidth
              required
              size="small"
              placeholder="https://..."
            />
            <TextField
              label="HTML Layout"
              value={form.htmlLayout}
              onChange={(e) =>
                setForm((p) => ({ ...p, htmlLayout: e.target.value }))
              }
              fullWidth
              required
              multiline
              rows={12}
              size="small"
              placeholder="<html>...</html>"
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: 13,
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ minHeight: 44 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !formValid ||
              createMutation.isPending ||
              updateMutation.isPending
            }
            sx={{ minHeight: 44 }}
          >
            {editingTemplate ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={!!deactivateConfirmId}
        onClose={() => setDeactivateConfirmId(null)}
      >
        <DialogTitle>Deactivate Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate this template? It will no longer
            be available for new events, but existing events using it will not be
            affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeactivateConfirmId(null)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() =>
              deactivateConfirmId && handleDeactivate(deactivateConfirmId)
            }
            disabled={deactivateMutation.isPending}
            sx={{ minHeight: 44 }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
